import { z } from 'zod';

// A non-project category (a standing time bucket like "Gen Admin" or "Leave").
// Every category is global — there is no team-scope field. `approverOnly` is
// the only scoping there is, and it scopes by ROLE, not by team.
//
// `approverOnly` is a ROUTING RULE, NOT A CONFIDENTIALITY BOUNDARY. It decides
// which timesheets the category is seeded onto; it decides nothing about who
// may read its name or its hours. This endpoint stays ungated and unfiltered on
// purpose: a retained line — one that already carries hours when the flag is
// flipped, or when an ordinary team edit demotes its owner — must keep its
// label, and the label comes from here. The accepted consequence is that a
// contributor's browser can enumerate approver-only category names, and their
// hours reach shared analytics unchanged. Categories holding genuinely
// confidential work are not what this flag is for.
//
// The client must NEVER use this flag to filter rows out of a timesheet. The
// server decides which lines exist; the grid renders whatever it was sent. A
// client-side filter here is the U-07 bug — "lines never disappear"
// (REQUIREMENTS.md) — and it would fire on the commonest case rather than an
// exotic one: demotion is a side effect of an ordinary team edit, so hours
// already logged would vanish from their owner's own sheet.
export const AdminCategoryItem = z.object({
  categoryId: z.string().uuid(),
  name: z.string(),
  // Work time (counts as work) vs non-work (time off, e.g. Leave).
  isWorkTime: z.boolean(),
  // false = deactivated (kept for history, not offered on new timesheets).
  activeFlag: z.boolean(),
  // Cutoff date (YYYY-MM-DD) after which no new hours may be charged, or null.
  lockedAt: z.string().nullable(),
  // true = seeded only onto approvers' and administrators' timesheets. See the
  // routing-rule note above before reading anything else into it.
  //
  // Defaulted rather than required so deploy skew degrades correctly: an SPA
  // newer than the API parses a response that predates this field and reads
  // "visible to everyone", which is precisely what an API without the field
  // means. A bare `z.boolean()` would instead throw and blank the whole
  // category catalog, taking every non-project row's label with it.
  approverOnly: z.boolean().default(false),
});
export type AdminCategoryItem = z.infer<typeof AdminCategoryItem>;

export const AdminCategoriesResponse = z.array(AdminCategoryItem);
export type AdminCategoriesResponse = z.infer<typeof AdminCategoriesResponse>;

// What a `→ approvers only` flip would do to the people who hold this category
// today, split by outcome. Two counts rather than one, because the outcomes
// genuinely differ and a single "N contributors affected" would be actively
// misleading: several of that N keep their row.
//
// Both counts are of DISTINCT USERS with no approver authority right now, over
// the rows they hold on editable (draft/returned) sheets. The sets are
// disjoint, and "retained" wins: somebody with hours on one week and an empty
// row on another is retaining, not losing, because the outcome they care about
// is that nothing they logged goes away.
export const AdminCategoryAudienceImpact = z.object({
  // People holding at least one line the sweep spares — it carries time entries
  // or a contributor note. The row is retained immediately, read-only and
  // badged (U-07 — logged hours never disappear), and a decrease still saves.
  retainingContributors: z.number().int().nonnegative(),
  // People whose rows are all empty and note-free. The flip takes the row away
  // at once: the rows they hold are swept and the seed stops creating more.
  losingContributors: z.number().int().nonnegative(),
});
export type AdminCategoryAudienceImpact = z.infer<typeof AdminCategoryAudienceImpact>;

export const CreateAdminCategoryRequest = z.object({
  name: z.string().min(1).max(255),
  isWorkTime: z.boolean(),
  // Optional (`approverOnly?: boolean | undefined` under
  // `exactOptionalPropertyTypes`), not defaulted here: omitting it leaves the
  // `admin_categories.approver_only` column default — visible to everyone — to
  // decide, so there is exactly one statement of what "unspecified" means
  // rather than a Zod copy that can drift from the column. A client that
  // predates the control therefore still creates a valid category.
  approverOnly: z.boolean().optional(),
});
export type CreateAdminCategoryRequest = z.infer<typeof CreateAdminCategoryRequest>;

// Edit fields and/or deactivate/reactivate and/or set-or-clear the lock cutoff
// and/or change who the category is seeded onto.
// All fields optional (partial patch). lockedAt: 'YYYY-MM-DD' to lock, null to unlock.
//
// `.partial()` under `exactOptionalPropertyTypes` gives every field the
// `field?: T | undefined` spelling, so an omitted key and an explicit
// `undefined` are the same "don't touch this" instruction. Send only the keys
// you mean to change: there is no version or If-Match on this PATCH, so a
// caller that round-trips a whole object silently reverts anything a colleague
// changed while its copy was stale.
export const UpdateAdminCategoryRequest = z
  .object({
    name: z.string().min(1).max(255),
    isWorkTime: z.boolean(),
    activeFlag: z.boolean(),
    lockedAt: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'lockedAt must be YYYY-MM-DD')
      .nullable(),
    // See AdminCategoryItem.approverOnly — a routing rule, not a privacy one.
    approverOnly: z.boolean(),
  })
  .partial();
export type UpdateAdminCategoryRequest = z.infer<typeof UpdateAdminCategoryRequest>;
